/**
 * Minimal vector math for the shared simulation.
 *
 * Kept dependency-free and engine-agnostic on purpose: this code runs on both
 * the Babylon client and (later) the headless Node server, so it must never
 * import a rendering engine's math types.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const vclone = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });

export const clamp = (n: number, lo: number, hi: number): number =>
  n < lo ? lo : n > hi ? hi : n;

/** Length of the horizontal (x/z) component only. */
export const horizLen = (a: Vec3): number => Math.hypot(a.x, a.z);

/**
 * Move `current` toward `target` by at most `maxDelta` (scalar 1D helper used
 * for acceleration/friction). Deterministic and allocation-free.
 */
export const moveTowards = (
  current: number,
  target: number,
  maxDelta: number,
): number => {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
};

/** Axis-aligned box collider in world space, defined by min/max corners. */
export interface AABB {
  min: Vec3;
  max: Vec3;
}

export const aabb = (
  cx: number,
  cz: number,
  halfX: number,
  halfZ: number,
  minY: number,
  maxY: number,
): AABB => ({
  min: { x: cx - halfX, y: minY, z: cz - halfZ },
  max: { x: cx + halfX, y: maxY, z: cz + halfZ },
});
